import React, { useEffect, useState } from 'react';
import { StyleSheet } from 'react-native';
import { Box, Typography, Button } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { ColorScheme, theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { EsShareButton, IShareButton } from '../EsButtons/EsShareButton';
import { EsBackButton } from '../EsButtons/EsBackButton';
import { TagsRenderer } from '../EsCards/TagsRenderer';
import { strings } from '../../constants/strings';
import { isEmpty } from '../../utils/lodash';
import { IContest, IProject, ISubmission } from '../../types/global/helper';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useProgressiveImage } from '../../hook/useProgressiveLoad';
import { CustomizedSwitches } from '../Switch/Switch';
import { IContestStatus } from '../../types/global/generic.types';
import { getJWtDetails } from '../../utils/AppUtils';
import active_bookmark from '../../asset/images/icons/active_bookmark.svg';
import inactive_bookmark from '../../asset/images/icons/inactive_bookmark.svg';
import { ReadMore } from '../ReadMore/ReadMore';
import { ToolTip } from '../Tip/ToolTip';
import CustomLazyLoadImage from '../Image/CustomLazyLoadImage';
import { RichTextViewer } from '../Common/RichTextViewer';
import { useDispatch } from 'react-redux';
import { setmedia } from '../../redux/project/actions';

export interface HeroMap extends IShareButton {
    campaign: IContest; //TODO Rename this to be contest
    onViewDetailsClick?: (docId?: string, isEvent?: boolean, contest?: IContest) => void;
    onJoinContestClick?: (docId: IContest) => void;
    isDetails?: boolean;
    onBackButtonPressed?: () => void;
    disableJoinContest?: boolean;
    userSubmissions?: ISubmission[];
    isAdminMode?: boolean;
    status?: IContestStatus;
    onToggleStatus?: (status: IContestStatus) => void;
    enableBookmark?: boolean;
    isAlreadyBookmarked?: boolean;
    onToggleBookmark?: (contest: string) => void;
}

const Hero: React.FC<HeroMap> = props => {
    const { themeStyle, device } = useThemeContext();
    const styles = dynamicStyles(themeStyle);
    const {
        campaign,
        onViewDetailsClick,
        isDetails,
        onBackButtonPressed,
        status,
        isAdminMode,
        onToggleStatus,
        enableBookmark,
        isAlreadyBookmarked,
        onToggleBookmark,
    } = props;
    /** Render the smaller Image in mobile screens */
    const selectedImage =
        device.isMobile && campaign.mobile_feature_image
            ? campaign.mobile_feature_image
            : campaign.contest_video?.thumbnail
            ? campaign.contest_video?.thumbnail
            : campaign?.feature_image;
    const { sourceLoaded, loaded } = useProgressiveImage(selectedImage);
    const [published, setPublished] = useState(false);
    const { isSignedIn } = getJWtDetails();
    const dispatch = useDispatch();
    const handleDetails = () => {
        if (onViewDetailsClick) {
            onViewDetailsClick(
                campaign.kind && campaign.kind === 'physical' ? campaign.slug : campaign.slug,
                campaign.kind && campaign.kind === 'physical',
                campaign,
            );
        }
    };

    useEffect(() => {
        if (status) {
            setPublished(status === 'published');
        }
    }, [status]);

    const handlePublishToggle = () => {
        const publishedStatus = published ? 'draft' : 'published';
        setPublished(!published);
        if (onToggleStatus) {
            onToggleStatus(publishedStatus);
        }
    };

    const handleVideoClick = (video: IProject) => {
        dispatch(
            setmedia({
                media: video,
                mediaType: 'video',
            }),
        );
    };

    return (
        <Box
            id="featured-contest"
            style={styles.box}
            sx={{
                backgroundImage: {
                    xs: `linear-gradient(to top, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.3)), url(${sourceLoaded})`,
                    sm: `linear-gradient(to right, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.3)), url(${sourceLoaded})`,
                },
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                height: { xs: '70vh', sm: '500px' },
                maxHeight: { xs: '440px', sm: '500px' },
                width: { xs: '100%', sm: '100%' },
                overflow: 'hidden',
                border: `0.2px solid rgba(255,255,255,0.3)`, // Change this in light tone
            }}>
            {enableBookmark && isSignedIn && (
                <Box sx={{ position: 'absolute', top: 40, right: { xs: 0, sm: 30 }, padding: '10px', zIndex: 10 }}>
                    <CustomLazyLoadImage
                        style={{ height: '30px', width: ' 30px', cursor: 'pointer' }}
                        src={isAlreadyBookmarked ? active_bookmark : inactive_bookmark}
                        alt={` ${isAlreadyBookmarked ? 'active bookmark' : 'inactive bookmark'}`}
                        onClick={() => {
                            onToggleBookmark && onToggleBookmark(campaign._id);
                        }}
                    />
                </Box>
            )}
            {isAdminMode && status && (
                <CustomizedSwitches
                    label={status === 'published' ? 'Unpublish' : 'Publish'}
                    style={{
                        position: 'absolute',
                        top: '20px',
                        right: '20px',
                        color: theme[themeStyle].true_white,
                    }}
                    switchProps={{
                        color: 'primary',
                        centerRipple: true,
                        checked: published,
                        value: published,
                        onChange: handlePublishToggle,
                    }}
                />
            )}
            {!isAdminMode && !isDetails && !!campaign && campaign.tag && !isEmpty(campaign.tag) && loaded && (
                <Box style={styles.flexColumn} sx={{ gap: '18px', marginLeft: '38px', marginBottom: '44px' }}>
                    <Box sx={{ display: 'flex' }}>
                        {campaign.tag.map((tag, index) => (
                            <Typography
                                key={index}
                                style={styles.textSm}
                                sx={{
                                    color: theme[themeStyle].true_white,
                                    width: 'fit-content',
                                    height: '26px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    textAlign: 'center',
                                    backgroundColor: theme[themeStyle].tag_white_light,
                                    borderRadius: 1,
                                    marginTop: { xs: '22px', sm: '0px' },
                                    mr: 1,
                                    px: 1,
                                }}>
                                {tag}
                            </Typography>
                        ))}
                    </Box>
                    <Box style={styles.flexColumn} sx={{ gap: '8px' }}>
                        <Typography variant="h1" style={styles.title}>
                            {campaign.title}
                        </Typography>
                        <RichTextViewer
                            content={campaign.description}
                            textColor={theme[themeStyle].true_white}
                            textStyle={{
                                padding: 0,
                                color: theme[themeStyle].true_white,
                                maxWidth: device.isMobile ? '80%' : '500px',
                                display: '-webkit-box',
                                overflow: 'hidden',
                                WebkitBoxOrient: 'vertical',
                                WebkitLineClamp: 3, //numOfLines
                            }}
                        />
                    </Box>
                    <Box style={styles.flexRow} sx={{ gap: '12px' }}>
                        {!!campaign.contest_video && campaign.contest_video.hls && (
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<PlayArrowIcon />}
                                onClick={() => handleVideoClick(campaign.contest_video as IProject)}
                                sx={{
                                    backgroundColor: 'white',
                                    color: 'black',
                                    '&:hover': {
                                        backgroundColor: '#e5e7eb',
                                    },
                                    fontSize: {
                                        xs: 10,
                                        sm: 12,
                                    },
                                }}>
                                Watch Campaign Video
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            size="large"
                            sx={{
                                borderColor: theme[themeStyle].grey,
                                color: theme[themeStyle].true_white,
                                '&:hover': {
                                    backgroundColor: theme[themeStyle].grey,
                                    opacity: 0.1,
                                },
                                fontSize: {
                                    xs: 10,
                                    sm: 12,
                                },
                            }}
                            onClick={handleDetails}>
                            Get Details
                        </Button>
                    </Box>
                </Box>
            )}

            {isDetails && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        paddingLeft: '38px',
                        paddingBottom: '22px',
                        paddingRight: '38px',
                        paddingTop: '22px',
                        width: '100%',
                        height: '100%',
                        overflow: 'scroll',
                        backgroundColor: theme[themeStyle].shadowColor,
                    }}>
                    {/* Back button */}
                    <EsBackButton handleDetails={onBackButtonPressed} />
                    <Box>
                        <Box
                            sx={
                                !isAdminMode
                                    ? {
                                          position: 'absolute',
                                          top: 20,
                                          right: 40,
                                          display: 'flex',
                                      }
                                    : {
                                          position: 'absolute',
                                          bottom: 20,
                                          right: 40,
                                          display: 'flex',
                                      }
                            }>
                            <Box display={'flex'}>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: theme[themeStyle].true_white,
                                        backgroundColor: theme[themeStyle].true_black,
                                        borderRadius: 0.5,
                                        px: 2,
                                        py: 0.5,
                                        fontSize: 10,
                                        textTransform: 'uppercase',
                                        border: `0.5px solid ${theme[themeStyle].highlight}`,
                                    }}>
                                    {campaign.submission_state === 'private_submission'
                                        ? 'Private Campaign'
                                        : 'Public Submissions'}
                                </Typography>
                                <ToolTip
                                    textBoxSx={{
                                        right: 0,
                                        bottom: -80,
                                        height: 100,
                                    }}
                                    text="When private, your submission should NOT be publicly visible. It should also not be on any platform and must not have been published before this campaign"
                                />
                            </Box>
                            {campaign.premium && (
                                <Box display={'flex'}>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.REGULAR_HEADER,
                                            color: theme[themeStyle].black,
                                            backgroundColor: theme[themeStyle].switchGreen,
                                            borderRadius: 0.5,
                                            px: 2,
                                            py: 0.5,
                                            fontSize: 10,
                                            textTransform: 'uppercase',
                                        }}>
                                        Premium
                                    </Typography>
                                    <ToolTip
                                        textBoxSx={
                                            !isAdminMode
                                                ? {
                                                      left: -150,
                                                      top: -0,
                                                      height: 70,
                                                  }
                                                : {
                                                      left: -150,
                                                      top: -80,
                                                      height: 70,
                                                  }
                                        }
                                        text="This is an invite only contest. Participants will need an invite code."
                                    />
                                </Box>
                            )}
                        </Box>
                    </Box>
                    <Box
                        style={styles.flexColumn}
                        sx={{ gap: { xs: '8px', sm: '34px' }, width: '100%', marginTop: { xs: '22px', sm: '0px' } }}>
                        <Box style={styles.flexColumn} sx={{ gap: '10px' }}>
                            <Typography
                                style={styles.textSm}
                                sx={{
                                    color: theme[themeStyle].true_white,
                                    width: '60px',
                                    height: '28px',
                                    paddingTop: '4px',
                                    paddingBottom: '4px',
                                    backgroundColor: theme[themeStyle].transparent,
                                    borderRadius: '8px',
                                    border: `1px solid ${theme[themeStyle].true_white}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}>
                                {campaign.rating}
                            </Typography>
                            <Typography
                                style={{
                                    fontSize: 24,
                                    fontWeight: '600',
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    color: theme[themeStyle].true_white,
                                }}>
                                {campaign.title}
                            </Typography>

                            <TagsRenderer
                                tags={campaign.tag}
                                maxTag={2}
                                iconFill={theme[themeStyle].true_white}
                                typographyStyle={{ color: theme[themeStyle].true_white }}
                            />
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                alignItems: 'center',
                                justifyContent: { xs: 'center', sm: 'space-between' },
                                gap: { xs: '22px', sm: '0px' },
                                width: '100%',
                            }}>
                            <Box style={styles.flexColumn} sx={{ gap: '8px' }}>
                                <RichTextViewer
                                    content={
                                        campaign.kind === 'physical' ? strings.about_event : strings.contest_description
                                    }
                                    textStyle={{
                                        padding: 0,
                                    }}
                                    textColor={theme[themeStyle].true_white}
                                />
                                <ReadMore
                                    maxChars={device.isMobile ? 100 : 200}
                                    textColor="#fff"
                                    text={campaign.description}
                                    header={campaign.title}
                                    style={{
                                        ...styles.text,
                                        color: theme[themeStyle].true_white,
                                        maxWidth: '500px',
                                    }}
                                />
                                {campaign.contest_video && campaign.contest_video.hls && (
                                    <Button
                                        variant="contained"
                                        size="large"
                                        startIcon={<PlayArrowIcon />}
                                        onClick={() => handleVideoClick(campaign.contest_video as IProject)}
                                        sx={{
                                            backgroundColor: 'white',
                                            color: 'black',
                                            '&:hover': {
                                                backgroundColor: '#e5e7eb',
                                            },
                                            fontSize: {
                                                xs: 10,
                                                sm: 12,
                                            },
                                        }}>
                                        Watch Campaign Video
                                    </Button>
                                )}
                            </Box>

                            {!isAdminMode && loaded && (
                                <Box style={styles.flexRow} sx={{ gap: '12px' }}>
                                    <EsShareButton {...props} />
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

const dynamicStyles = (colorScheme: ColorScheme) =>
    StyleSheet.create({
        box: {
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'flex-start',
            position: 'relative',
            borderRadius: 10,
        },
        detailsButton: {
            height: '40px',
            padding: '10px',
            borderRadius: 100,
            fontSize: 14,
            fontWeight: '600',
            fontFamily: FONT_ENUM.MAIN_BOLD,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
        },
        bgImage: {
            position: 'absolute',
            top: 0,
            bottom: 0,
            right: 0,
            left: 0,
            width: 'inherit',
            height: 'inherit',
            zIndex: -1,
        },
        text: {
            fontSize: 14,
            fontWeight: '500',
            fontFamily: FONT_ENUM.MAIN_MEDIUM,
        },
        textSm: {
            fontSize: 12,
            fontWeight: '500',
            fontFamily: FONT_ENUM.MAIN_MEDIUM,
        },
        title: {
            fontSize: 30,
            fontWeight: '600',
            fontFamily: FONT_ENUM.MAIN_BOLD,
            color: theme[colorScheme].true_white,
        },
        challenge: {
            fontSize: 24,
            fontWeight: '600',
            fontFamily: FONT_ENUM.MAIN_BOLD,
            color: theme[colorScheme].filters_white,
        },
        flexRow: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
        },
        flexRowBetween: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
        },
        flexColumn: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
        },
    });

export { Hero };
